import type { ComponentType } from "react";

import { defineMessages, injectIntl } from "react-intl";
import type { intlFormatPropTypes } from "react-intl";
import { useSelector } from "react-redux";
import { browserHistory } from "react-router";

import { Item, ItemGroup } from "@ps-design-system/components/ItemV2";
import CalendarIcon from "@ps-design-system/icons/Calendar/Calendar24";
import EventIcon from "@ps-design-system/icons/Event/Event24";
import InstantBookIcon from "@ps-design-system/icons/InstantBook/InstantBook24";
import VideoIcon from "@ps-design-system/icons/Video/Video24";

import { selectProfileSSOid } from "../../app-main/selectors/profile.selectors";
import { Links } from "../../constants/links.consts";
import { trackRecommendedItemClicked } from "../analytics/host-hub-analytics";
import type {
  RecommendedCardData,
  RecommendedCardItem,
  RecommendedItemKey,
} from "../types/model/recommended";

const recommendedItemMessages = defineMessages({
  add_video_tour_title: {
    id: "host_hub.todo_list.video_tours_title",
    defaultMessage: "Add video tours to your listings",
  },
  add_video_tour_description: {
    id: "host_hub.todo_list.video_tours_subtitle",
    defaultMessage:
      "Add videos and a 3D tour to help guests picture your space.",
  },
  event_faq_title: {
    id: "host_hub.todo_list.event_faq_title",
    defaultMessage: "Fill out your event FAQ",
  },
  event_faq_description: {
    id: "host_hub.todo_list.event_faq_subtitle",
    defaultMessage:
      "Save time by answering common questions once in your Event FAQ",
  },
  calendar_sync_title: {
    id: "host_hub.todo_list.calendar_sync_title",
    defaultMessage: "Keep your calendar up-to-date by syncing",
  },
  calendar_sync_description: {
    id: "host_hub.todo_list.calendar_sync_subtitle",
    defaultMessage:
      "Hosts who sync their calendar attract more qualified guests, spend less time messaging, and improve their space's visibility in search results.",
  },
  enable_instant_book_title: {
    id: "host_hub.todo_list.instant_book_title",
    defaultMessage: "Enable Instant Book",
  },
  enable_instant_book_description: {
    id: "host_hub.todo_list.instant_book_subtitle",
    defaultMessage:
      "Hosts using Instant Book convert 2x more bookings than those who aren't.",
  },
});

type RecommendedItemDisplay = {
  Icon: ComponentType<{ className?: string }>;
  title: (typeof recommendedItemMessages)[keyof typeof recommendedItemMessages];
  description?: (typeof recommendedItemMessages)[keyof typeof recommendedItemMessages];
};

const RECOMMENDED_ITEM_DISPLAY: Record<
  RecommendedItemKey,
  RecommendedItemDisplay
> = {
  add_video_tour: {
    Icon: VideoIcon,
    title: recommendedItemMessages.add_video_tour_title,
    description: recommendedItemMessages.add_video_tour_description,
  },
  event_faq: {
    Icon: EventIcon,
    title: recommendedItemMessages.event_faq_title,
    description: recommendedItemMessages.event_faq_description,
  },
  calendar_sync: {
    Icon: CalendarIcon,
    title: recommendedItemMessages.calendar_sync_title,
    description: recommendedItemMessages.calendar_sync_description,
  },
  enable_instant_book: {
    Icon: InstantBookIcon,
    title: recommendedItemMessages.enable_instant_book_title,
    description: recommendedItemMessages.enable_instant_book_description,
  },
};

type HostHubRecommendedItemProps = {
  card: RecommendedCardData;
  intl: intlFormatPropTypes;
};

const HostHubRecommendedItem = ({
  card,
  intl,
}: HostHubRecommendedItemProps) => {
  const ssoid = useSelector(selectProfileSSOid);
  const { avatarAlt, avatarUrl, items, key, mediaAlt, mediaUrl } = card;
  const display = RECOMMENDED_ITEM_DISPLAY[key];
  const { Icon } = display;

  const title = intl.formatMessage(display.title);
  const description = display.description
    ? intl.formatMessage(display.description)
    : undefined;

  const navigate = (route: string) => {
    trackRecommendedItemClicked({ parameter: key, ssoid });
    browserHistory.push(route);
  };

  const navigateToItem = (item: RecommendedCardItem) =>
    navigate(item.primaryCtaRoute);

  // Calendar sync always renders as a single item — never a group — regardless
  // of how many spaces are unsynced. With one space we deep-link to it; with
  // several there's no single target, so we open the calendar page.
  if (key === "calendar_sync") {
    const route =
      items && items.length === 1 ? items[0].primaryCtaRoute : Links.Calendar;

    return (
      <Item
        buttonOnClick={() => navigate(route)}
        description={description}
        icon={<Icon />}
        size="lg"
        title={title}
        variant="outline"
      />
    );
  }

  // Every other recommended card renders as a group with one row per listing,
  // even when there's only a single listing.
  if (items && items.length > 0) {
    return (
      <ItemGroup defaultExpanded size="lg" variant="outline">
        <Item description={description} icon={<Icon />} title={title} />
        {items.map((item) =>
          item.mediaUrl ? (
            <Item
              buttonOnClick={() => navigateToItem(item)}
              key={item.id}
              media={item.mediaUrl}
              mediaAlt={item.mediaAlt}
              mediaSize={56}
              title={item.title}
            />
          ) : (
            <Item
              buttonOnClick={() => navigateToItem(item)}
              icon={<Icon />}
              key={item.id}
              title={item.title}
            />
          ),
        )}
      </ItemGroup>
    );
  }

  // Recommended card without per-listing rows.
  const mediaProps = mediaUrl
    ? { avatar: avatarUrl, avatarAlt, media: mediaUrl, mediaAlt }
    : { icon: <Icon /> };

  return (
    <Item
      buttonOnClick={() => {}}
      description={description}
      mediaSize={40}
      size="lg"
      title={title}
      variant="outline"
      {...mediaProps}
    />
  );
};

export default injectIntl(HostHubRecommendedItem);
